import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient.js';

// Google Sheet ID for the master booked work sheet
const SHEET_ID = '1mF5G3dY3aa8eJLb-TTRNCmm6B3NGtXa5Yf2BaD9F56s';
const SHEETS_API_KEY = process.env.REACT_APP_GOOGLE_SHEETS_API_KEY;
const WRITEBACK_URL = process.env.REACT_APP_SHEETS_WRITEBACK_URL;

const BookedWorkPage = () => {
  const [jobs, setJobs] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [applicators, setApplicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [assigningId, setAssigningId] = useState(null);

  const fetchJobs = useCallback(async () => {
    const [bookedRes, assignedRes, routesRes, appRes] = await Promise.all([
      supabase.from('crm_jobs')
        .select('*')
        .eq('booking_status', 'booked')
        .order('company', { ascending: true }),
      supabase.from('route_items')
        .select('crm_job_id')
        .not('crm_job_id', 'is', null)
        .not('route_id', 'is', null),
      supabase.from('routes')
        .select('id, route_name, corridor, start_date, applicator_id')
        .in('status', ['draft', 'active'])
        .order('start_date', { ascending: true }),
      supabase.from('applicators').select('id, name').not('user_id', 'is', null),
    ]);

    const assignedSet = new Set((assignedRes.data || []).map(r => r.crm_job_id));
    setJobs((bookedRes.data || []).filter(j => !assignedSet.has(j.id)));
    if (routesRes.data) setRoutes(routesRes.data);
    if (appRes.data) setApplicators(appRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchJobs();

    const channel = supabase
      .channel('booked-work')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_jobs' }, () => fetchJobs())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'route_items' }, () => fetchJobs())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchJobs]);

  // ─── Sync from Google Sheets → crm_jobs ──────────────────────
  // Uses sync_key (UUID) as the stable matching key instead of sheet_row.
  // sheet_row is still stored but updated to current position each sync (for writeback).
  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);

    try {
      // Fetch live data from Google Sheets API
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}/values/A:Z?key=${SHEETS_API_KEY}`;
      // Fetch values + hyperlinks in parallel
      const [valuesRes, linksRes] = await Promise.all([
        fetch(url),
        fetch(`https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}?ranges=A:Z&fields=sheets.data.rowData.values.hyperlink&key=${SHEETS_API_KEY}`),
      ]);
      if (!valuesRes.ok) throw new Error(`Google Sheets API returned ${valuesRes.status}`);
      const json = await valuesRes.json();
      const rows = json.values;

      // Parse hyperlink data — gives us the actual URLs behind clickable text
      let hyperlinks = {};
      try {
        const linksJson = await linksRes.json();
        const rowData = linksJson?.sheets?.[0]?.data?.[0]?.rowData || [];
        rowData.forEach((rd, rowIdx) => {
          if (!rd.values) return;
          rd.values.forEach((cell, colIdx) => {
            if (cell.hyperlink) {
              if (!hyperlinks[rowIdx]) hyperlinks[rowIdx] = {};
              hyperlinks[rowIdx][colIdx] = cell.hyperlink;
            }
          });
        });
      } catch { /* hyperlinks are best-effort */ }

      if (!rows || rows.length < 2) {
        setSyncResult({ error: 'No data rows found' });
        setSyncing(false);
        return;
      }

      // Parse headers (first row)
      const headers = rows[0].map(h => h.toLowerCase().trim());

      // Find column indices
      const col = (name) => headers.findIndex(h => h.includes(name));
      const syncIdCol = headers.findIndex(h => h.includes('sync') && h.includes('id'));
      const statusCol = col('status');
      const companyCol = col('company');
      const contactCol = col('contact') !== -1 && !headers[col('contact')].includes('email') && !headers[col('contact')].includes('date')
        ? col('contact') : headers.findIndex(h => h === 'contact');
      const emailCol = headers.findIndex(h => h.includes('email') && h.includes('contact'));
      const phoneCol = col('phone');
      const workTypeCol = col('work type');
      const siteDescCol = col('site desc');
      const locationCol = col('location');
      const clientNotesCol = col('client notes');
      const siteNotesCol = col('site notes');
      const amountCol = col('amount');
      const hoursCol = col('time');
      const acresCol = col('area');
      const lastCompCol = col('last completed');
      const oprecCol = col('oprec');
      const quoteCol = col('quote');
      const kmzCol = col('kmz');
      const chemCol = col('chem');
      const imageCol = col('image');

      if (syncIdCol < 0) {
        throw new Error('Sync ID column not found in sheet headers. Add a "Sync ID" column header.');
      }

      const parseNum = (val) => {
        if (!val) return null;
        const n = parseFloat(val.replace(/[$,]/g, ''));
        return isNaN(n) ? null : n;
      };
      const parseYear = (val) => {
        if (!val) return null;
        const n = parseInt(val);
        return (n >= 2000 && n <= 2100) ? n : null;
      };

      // ── Bridge: match unkeyed crm_jobs to sheet rows by company+location ──
      // Handles two cases:
      //   A) Sheet has a UUID but no DB record has it → match by company+location, assign that UUID
      //   B) Sheet has no UUID → generate one, match by company+location, assign it
      const { data: existingJobs } = await supabase
        .from('crm_jobs')
        .select('id, company, location, sync_key');

      // Records that already have a sync_key (keyed by sync_key for quick lookup)
      const existingBySync = {};
      // Records without a sync_key (keyed by company+location for matching)
      const unkeyedByData = {};
      // Already-keyed records indexed by company+location, for healing a blanked
      // Sync ID. Stored as arrays so we can detect ambiguous matches (a single
      // company can legitimately have many sites) and refuse to rebind those.
      const keyedByData = {};
      for (const job of (existingJobs || [])) {
        const key = `${(job.company || '').toLowerCase().trim()}|${(job.location || '').toLowerCase().trim()}`;
        if (job.sync_key) {
          existingBySync[job.sync_key] = job;
          (keyedByData[key] = keyedByData[key] || []).push(job);
        } else {
          if (!unkeyedByData[key]) unkeyedByData[key] = job;
        }
      }

      // Build all upsert rows — sync_key is the stable unique key
      const allRows = [];
      const newSyncKeys = []; // rows that need sync_key written back to the sheet
      const sheetKeysSoFar = new Set(); // sync_keys consumed by rows processed so far this sync

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i].map(v => (v || '').trim());
        const company = companyCol >= 0 ? row[companyCol] : '';
        if (!company) continue;
        const status = statusCol >= 0 ? row[statusCol]?.toLowerCase() : '';
        const location = locationCol >= 0 ? row[locationCol] || '' : '';
        const matchKey = `${company.toLowerCase().trim()}|${location.toLowerCase().trim()}`;

        // Strip all whitespace (incl. NBSP, zero-width chars, BOM) from Sync ID.
        // Stealth chars copy-pasted into the cell survive .trim() and cause
        // "ON CONFLICT DO UPDATE command cannot affect row a second time" on upsert.
        let syncKey = syncIdCol >= 0
          ? (row[syncIdCol] || '').replace(/[\s\u00A0\u200B-\u200F\u2028-\u202F\u205F-\u206F\uFEFF]/g, '')
          : '';

        if (syncKey && existingBySync[syncKey]) {
          // Happy path: sheet UUID matches a DB record. Nothing to bridge.
        } else if (syncKey && !existingBySync[syncKey]) {
          // Case A: Sheet has UUID but no DB record has it.
          // Match by company+location and assign the sheet's UUID to the existing record.
          const match = unkeyedByData[matchKey];
          if (match) {
            await supabase.from('crm_jobs').update({ sync_key: syncKey }).eq('id', match.id);
            existingBySync[syncKey] = match; // now it's keyed
            delete unkeyedByData[matchKey];
          }
          // If no match, the upsert will INSERT (legitimate new job)
        } else {
          // Case B: Sheet has no UUID.
          const unkeyedMatch = unkeyedByData[matchKey];
          const keyedMatch = keyedByData[matchKey];
          if (unkeyedMatch) {
            // Adopt an existing manual (unkeyed) row: mint a UUID and assign it.
            syncKey = crypto.randomUUID();
            await supabase.from('crm_jobs').update({ sync_key: syncKey }).eq('id', unkeyedMatch.id);
            delete unkeyedByData[matchKey];
          } else if (keyedMatch && keyedMatch.length === 1 && !sheetKeysSoFar.has(keyedMatch[0].sync_key)) {
            // Heal a blanked Sync ID: reuse the existing keyed row's UUID instead of
            // minting a new one (which would insert a duplicate and orphan the old row).
            syncKey = keyedMatch[0].sync_key;
          } else {
            // No safe match (none, or ambiguous company+location). Mint a new UUID → new row.
            syncKey = crypto.randomUUID();
          }
          newSyncKeys.push({ sheetRow: i, syncKey });
        }

        // Record every key consumed this sync so a later blank-Sync-ID row can't
        // re-claim a keyed row that an earlier row already used (rebind guard).
        sheetKeysSoFar.add(syncKey);

        allRows.push({
          sync_key: syncKey,
          sheet_row: i, // current position — updated every sync, used only for writeback
          company,
          contact_name: contactCol >= 0 ? row[contactCol] || null : null,
          email: emailCol >= 0 ? row[emailCol] || null : null,
          phone: phoneCol >= 0 ? row[phoneCol] || null : null,
          work_type: workTypeCol >= 0 ? row[workTypeCol] || null : null,
          site_description: siteDescCol >= 0 ? row[siteDescCol] || null : null,
          location: locationCol >= 0 ? row[locationCol] || null : null,
          client_notes: clientNotesCol >= 0 ? row[clientNotesCol] || null : null,
          internal_notes: siteNotesCol >= 0 ? row[siteNotesCol] || null : null,
          value: amountCol >= 0 ? parseNum(row[amountCol]) : null,
          estimated_hours: hoursCol >= 0 ? parseNum(row[hoursCol]) : null,
          area_acres: acresCol >= 0 ? parseNum(row[acresCol]) : null,
          last_completed_year: lastCompCol >= 0 ? parseYear(row[lastCompCol]) : null,
          booking_status: status || 'not booked',
          oprec_link: (oprecCol >= 0 && hyperlinks[i]?.[oprecCol]) || (oprecCol >= 0 ? row[oprecCol] || null : null),
          quote_link: (quoteCol >= 0 && hyperlinks[i]?.[quoteCol]) || (quoteCol >= 0 ? row[quoteCol] || null : null),
          kmz_link: (kmzCol >= 0 && hyperlinks[i]?.[kmzCol]) || (kmzCol >= 0 ? row[kmzCol] || null : null),
          image_link: (imageCol >= 0 && hyperlinks[i]?.[imageCol]) || (imageCol >= 0 ? row[imageCol] || null : null),
          chemicals_summary: chemCol >= 0 ? row[chemCol] || null : null,
        });
      }

      // Guard: Postgres rejects the whole upsert batch if two rows share the
      // conflict target (sync_key). Detect it here so we can point the user at
      // the exact offending sheet rows instead of a cryptic DB error.
      const seenKeys = new Map();
      const duplicateGroups = [];
      for (const r of allRows) {
        if (seenKeys.has(r.sync_key)) {
          const first = seenKeys.get(r.sync_key);
          duplicateGroups.push({ sync_key: r.sync_key, rows: [first, r] });
        } else {
          seenKeys.set(r.sync_key, r);
        }
      }
      if (duplicateGroups.length > 0) {
        console.error('[Sync] Duplicate sync_key values in batch:');
        for (const g of duplicateGroups) {
          const codes = [...g.sync_key].map(c => c.charCodeAt(0)).join(',');
          console.error(`  sync_key="${g.sync_key}" (codes=[${codes}])`);
          for (const r of g.rows) {
            console.error(`    sheet_row=${r.sheet_row + 1} company="${r.company}" location="${r.location || ''}"`);
          }
        }
        const rowList = duplicateGroups
          .map(g => g.rows.map(r => r.sheet_row + 1).join(' & '))
          .join('; ');
        throw new Error(
          `Duplicate Sync ID detected in sheet rows: ${rowList}. ` +
          `Open the sheet and make each "Sync ID" value unique (or clear one to regenerate). See console for details.`
        );
      }

      // Upsert using sync_key as the stable unique key
      const { error: upsertError } = await supabase
        .from('crm_jobs')
        .upsert(allRows, { onConflict: 'sync_key' });

      if (upsertError) {
        throw new Error(`Upsert failed: ${upsertError.message}`);
      }

      // ── Reconcile: prune synced rows that no longer exist in the sheet ──
      // The sheet is the source of truth. A crm_job whose sync_key is absent from
      // the sheet is a stale orphan (its Sync ID was blanked/changed, or its row was
      // deleted). The upsert above never removes these, so they accumulate as
      // duplicates on the Booked Work page. Prune them here — but only the ones that
      // are safe to delete (keyed, gone from the sheet, and not on any route).
      const sheetSyncKeys = new Set(allRows.map(r => r.sync_key).filter(Boolean));
      let reconcileNote = '';
      const { data: keyedRows } = await supabase
        .from('crm_jobs')
        .select('id, sync_key, company, location')
        .not('sync_key', 'is', null);

      // Safety floor: never prune on a thin/partial sheet read. A truncated fetch
      // could otherwise flag most live rows as orphans and wipe the table.
      const FLOOR_MIN = 5;
      const FLOOR_RATIO = 0.5;
      const reconcileSafe = sheetSyncKeys.size >= FLOOR_MIN
        && sheetSyncKeys.size >= (keyedRows || []).length * FLOOR_RATIO;

      if (reconcileSafe) {
        const orphans = (keyedRows || []).filter(r => !sheetSyncKeys.has(r.sync_key));
        const orphanIds = orphans.map(r => r.id);
        let referencedIds = new Set();
        if (orphanIds.length > 0) {
          // crm_job_id FK is ON DELETE SET NULL — deleting a referenced job would
          // silently dangle its route_item, so never delete rows that are on a route.
          const { data: refs } = await supabase
            .from('route_items')
            .select('crm_job_id')
            .in('crm_job_id', orphanIds);
          referencedIds = new Set((refs || []).map(r => r.crm_job_id));
        }
        const deletableIds = orphans.filter(r => !referencedIds.has(r.id)).map(r => r.id);
        const onRoute = orphans.filter(r => referencedIds.has(r.id));

        if (deletableIds.length > 0) {
          const { error: delError } = await supabase
            .from('crm_jobs')
            .delete()
            .in('id', deletableIds);
          if (delError) {
            console.warn('[Sync] Reconcile delete failed:', delError.message);
          } else {
            reconcileNote += ` Removed ${deletableIds.length} stale row(s) no longer in sheet.`;
          }
        }
        if (onRoute.length > 0) {
          reconcileNote += ` ${onRoute.length} stale row(s) are on a route and were NOT removed: `
            + onRoute.map(r => `${r.company}${r.location ? ' / ' + r.location : ''}`).join('; ')
            + ` — remove from route or re-add to sheet.`;
          console.warn('[Sync] Orphaned-but-on-route crm_jobs (not deleted):', onRoute);
        }
      } else {
        console.warn('[Sync] Reconcile skipped (safety floor): sheet returned too few keyed rows', {
          sheetSyncKeys: sheetSyncKeys.size,
          keyedRows: (keyedRows || []).length,
        });
      }

      // Write back generated sync_keys to the Google Sheet (fire-and-forget)
      if (WRITEBACK_URL && newSyncKeys.length > 0) {
        console.log(`[Sync] Writing ${newSyncKeys.length} new sync keys back to sheet...`);
        for (const { sheetRow, syncKey } of newSyncKeys) {
          fetch(WRITEBACK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sheetId: SHEET_ID,
              sheetRow,
              updates: { sync_key: syncKey },
            }),
          }).catch(err => console.warn('[Sync] Failed to write back sync key:', err));
        }
      }

      const msg = (newSyncKeys.length > 0
        ? `Synced: ${allRows.length} rows updated. ${newSyncKeys.length} new Sync IDs assigned.`
        : `Synced: ${allRows.length} rows updated from sheet`) + reconcileNote;
      setSyncResult({ upserted: allRows.length, newKeys: newSyncKeys.length, error: null, message: msg });
      fetchJobs();
    } catch (err) {
      console.error('[Sync] Failed:', err);
      setSyncResult({ error: err.message });
    }

    setSyncing(false);
  };

  // ─── Assign job to route ──────────────────────────
  const handleAssignToRoute = async (crmJobId, routeId) => {
    if (!routeId) return;
    setAssigningId(crmJobId);

    // Check if already assigned (ignore orphaned route_items with null route_id)
    const { data: alreadyAssigned } = await supabase
      .from('route_items')
      .select('id')
      .eq('crm_job_id', crmJobId)
      .not('route_id', 'is', null)
      .limit(1);
    if (alreadyAssigned?.length > 0) {
      setAssigningId(null);
      fetchJobs();
      return;
    }

    // Get max sort_order for day 1 of that route
    const { data: existing } = await supabase
      .from('route_items')
      .select('sort_order')
      .eq('route_id', routeId)
      .eq('day_number', 1)
      .order('sort_order', { ascending: false })
      .limit(1);

    const nextOrder = (existing?.[0]?.sort_order || 0) + 1;

    const { error } = await supabase
      .from('route_items')
      .insert([{
        crm_job_id: crmJobId,
        route_id: routeId,
        row_type: 'JOB',
        day_number: 1,
        sort_order: nextOrder,
      }]);

    if (error) {
      console.error('Error assigning job:', error);
      setAssigningId(null);
      return;
    }
    setAssigningId(null);
    fetchJobs();
  };

  if (loading) {
    return <div className="text-gray-300">Loading booked work...</div>;
  }

  const getApplicatorName = (id) => applicators.find(a => a.id === id)?.name;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Booked Work</h1>
          <p className="text-sm text-gray-400 mt-1">
            {jobs.length} booked jobs — ${jobs.reduce((sum, j) => sum + (parseFloat(j.value) || 0), 0).toLocaleString()} total
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-900 disabled:opacity-50 transition-colors"
        >
          {syncing ? 'Syncing...' : 'Sync'}
        </button>
      </div>

      {/* Sync result */}
      {syncResult && (
        <div className={`mb-4 px-4 py-2 rounded-md text-sm ${
          syncResult.error
            ? 'bg-red-500/20 border border-red-500 text-red-200'
            : 'bg-green-600/20 border border-green-600 text-green-400'
        }`}>
          {syncResult.error
            ? `Sync failed: ${syncResult.error}`
            : syncResult.message || `Synced: ${syncResult.upserted} rows updated from sheet`
          }
        </div>
      )}

      {/* Job list */}
      {jobs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-400 text-lg">No unassigned booked work.</p>
          <p className="text-gray-400 text-sm mt-1">Click "Sync" to pull in booked jobs.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map((job) => (
            <div key={job.id} className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold truncate">{job.company}</h3>
                    {job.work_type && (
                      <span className="text-xs px-1.5 py-0.5 bg-gray-500/50 text-gray-300 rounded">{job.work_type}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-300">
                    {job.location && <span>{job.location}</span>}
                    {job.contact_name && <span>{job.contact_name}</span>}
                    {job.value && <span className="text-green-400">${parseFloat(job.value).toLocaleString()}</span>}
                    {job.estimated_hours && <span>{job.estimated_hours}h</span>}
                    {job.area_acres && <span>{job.area_acres} acres</span>}
                    {job.last_completed_year && <span className="text-gray-400">Last: {job.last_completed_year}</span>}
                  </div>
                  {job.site_description && (
                    <p className="text-xs text-gray-400 mt-1">{job.site_description}</p>
                  )}
                  {job.client_notes && (
                    <p className="text-xs text-amber-300 mt-1">{job.client_notes}</p>
                  )}
                </div>

                {/* Assign to route */}
                <div className="shrink-0 flex items-center gap-2">
                  <select
                    onChange={(e) => handleAssignToRoute(job.id, e.target.value)}
                    disabled={assigningId === job.id}
                    defaultValue=""
                    className="text-xs bg-gray-900 border border-gray-500 text-gray-300 rounded px-2 py-1.5 focus:ring-1 focus:ring-green-600 focus:outline-none"
                  >
                    <option value="" disabled>Assign to route...</option>
                    {routes.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.route_name}
                        {r.applicator_id ? ` (${getApplicatorName(r.applicator_id) || '?'})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookedWorkPage;
